import { clearAllGeolocationWatches } from './geolocationWatchRegistry';

/** Custom event name for native hosts that inject a `window` listener. */
export const NATIVE_WEB_VIEW_PAGE_HIDE_EVENT = 'cntxt:native-webview-page-hide';

/**
 * `WKScriptMessageHandler` name used by CNTXT managed-browser hosts for fire-and-forget
 * lifecycle signals. Must not use reply handlers — those fail when the web view is gone.
 */
export const NATIVE_WEB_VIEW_LIFECYCLE_HANDLER = 'cntxtLifecycle';

let isPageUnloading = false;
let lifecycleHandlersInstalled = false;

/**
 * Whether the page is unloading (e.g. WKWebView teardown or bfcache `pagehide`).
 */
export function isNativeWebViewPageUnloading(): boolean {
  return isPageUnloading;
}

function notifyNativeHostPageHide(): void {
  const win = globalThis.window;
  if (win === undefined) {
    return;
  }

  try {
    win.dispatchEvent(new Event(NATIVE_WEB_VIEW_PAGE_HIDE_EVENT));
  } catch {
    // Ignore if the host environment rejects custom events during teardown.
  }

  const messageHandlers = (
    win as Window & {
      webkit?: {
        messageHandlers?: Record<
          string,
          { postMessage: (body: unknown) => void }
        >;
      };
    }
  ).webkit?.messageHandlers;

  const lifecycleHandler = messageHandlers?.[NATIVE_WEB_VIEW_LIFECYCLE_HANDLER];

  if (lifecycleHandler === undefined) {
    return;
  }

  try {
    lifecycleHandler.postMessage({ type: 'pagehide' });
  } catch {
    // Fire-and-forget: host may already be tearing down the web view.
  }
}

function handlePageHide(): void {
  if (isPageUnloading) {
    return;
  }

  isPageUnloading = true;
  clearAllGeolocationWatches();
  notifyNativeHostPageHide();
}

/**
 * Installs one-time `pagehide` handling for WKWebView / managed-browser hosts.
 * Clears geolocation watches and notifies native code to stop bridge traffic.
 */
export function installNativeWebViewLifecycleHandlers(): void {
  if (lifecycleHandlersInstalled || globalThis.window === undefined) {
    return;
  }

  lifecycleHandlersInstalled = true;
  globalThis.window.addEventListener('pagehide', handlePageHide, {
    capture: true,
  });
}
